import { generatePath } from "react-router";

import { NotificationContentType } from "@vega-api/generated/types/notificationContentType";
import { type NotificationResponse } from "@vega-api/generated/types/notificationResponse";
import {
  CoInvestmentFullPaths,
  VegaModulesPaths,
  VegaModulesWorkflowTasksPaths,
} from "@vega-modules/paths";
import {
  TRACK_VIEW_PARAM,
  TrackViewType,
} from "@vega-modules/scenes/CoInvestmentsGP/constants/trackPageConstants";

type DispatchablePayload = {
  type: NotificationResponse["type"];
  meta?: NotificationResponse["meta"];
};

const gpTrackPath = (dealId: string, view: TrackViewType) =>
  `${generatePath(CoInvestmentFullPaths.CoInvestmentTrack, {
    id: dealId,
  })}?${TRACK_VIEW_PARAM}=${view}`;

export const dispatchNotification = (
  payload: DispatchablePayload,
): string | null => {
  switch (payload.type) {
    case NotificationContentType.GP_WORKFLOW_TASK_ASSIGNMENT: {
      const subscriptionId = payload.meta?.subscriptionId;
      const workflowTaskId = payload.meta?.workflowTaskId;

      if (!subscriptionId || !workflowTaskId) return null;

      return generatePath(VegaModulesWorkflowTasksPaths.SubscriptionTask, {
        subscriptionId,
        workflowTaskId,
      });
    }

    case NotificationContentType.GP_INTERNAL_NOTE_TAG: {
      const subscriptionId = payload.meta?.subscriptionId;
      const fundId = payload.meta?.fundId;
      const commentId = payload.meta?.commentId;

      if (!subscriptionId || !fundId) return null;

      const questionnairePath = generatePath(
        VegaModulesPaths.FundSubscriptionQuestionnaire,
        { fundId, subscriptionId },
      );

      return commentId
        ? `${questionnairePath}?commentId=${commentId}`
        : questionnairePath;
    }

    case NotificationContentType.COINVEST_NOTIFICATION_ORDER_SUBMITTED:
    case NotificationContentType.COINVEST_NOTIFICATION_ALLOCATION_ACCEPTED:
    case NotificationContentType.COINVEST_NOTIFICATION_ALLOCATION_REJECTED: {
      const dealId = payload.meta?.dealId;

      if (!dealId) return null;

      return gpTrackPath(dealId, TrackViewType.Allocation);
    }

    case NotificationContentType.COINVEST_NOTIFICATION_NDA_SIGNED:
    case NotificationContentType.COINVEST_NOTIFICATION_DATA_ROOM_FIRST_ACCESS: {
      const dealId = payload.meta?.dealId;

      if (!dealId) return null;

      return gpTrackPath(dealId, TrackViewType.Process);
    }

    case NotificationContentType.COINVEST_NOTIFICATION_ALLOCATION_SENT:
    case NotificationContentType.COINVEST_NOTIFICATION_ORDER_REJECTED:
    case NotificationContentType.COINVEST_NOTIFICATION_DEAL_CLOSED:
    case NotificationContentType.COINVEST_NOTIFICATION_NDA_SENT:
    case NotificationContentType.COINVEST_NOTIFICATION_NDA_CONFIRMED:
    case NotificationContentType.COINVEST_NOTIFICATION_DEAL_ACCESS_GRANTED: {
      const opportunityId = payload.meta?.dealOpportunityId;

      if (!opportunityId) return null;

      return generatePath(CoInvestmentFullPaths.CoInvestmentLpOverview, {
        id: opportunityId,
      });
    }

    case NotificationContentType.COINVEST_NOTIFICATION_DATA_ROOM_ACCESS_GRANTED: {
      const opportunityId = payload.meta?.dealOpportunityId;

      if (!opportunityId) return null;

      return generatePath(CoInvestmentFullPaths.CoInvestmentLpDataRoom, {
        id: opportunityId,
      });
    }

    default:
      return null;
  }
};
